import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Star, Shield, Users } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { WhyChooseUs } from '../components/WhyChooseUs';
import { CTABanner } from '../components/CTABanner';
const values = [{
  icon: Heart,
  title: 'Compassion',
  description: 'We approach every interaction with genuine empathy, kindness, and a deep understanding of the emotional aspects of care.'
}, {
  icon: Star,
  title: 'Excellence',
  description: 'We are committed to the highest standards of clinical quality, continuous learning, and professional development.'
}, {
  icon: Shield,
  title: 'Transparency',
  description: 'We build trust through open, honest communication with our patients, their families, and our healthcare partners.'
}, {
  icon: Users,
  title: 'Dignity',
  description: 'We honor the individuality and life experiences of those we serve, ensuring respect is at the core of all we do.'
}];
const team = [{
  name: 'Dr. Sarah Jenkins',
  role: 'Chief Medical Officer',
  bio: 'With over 20 years of experience in geriatric medicine, Dr. Jenkins leads our clinical team with a focus on holistic, patient-centered care.',
  image: 'https://i.pravatar.cc/300?img=47'
}, {
  name: 'Michael Chen, RN',
  role: 'Director of Nursing',
  bio: 'Michael ensures our nursing staff delivers exceptional care, drawing from his extensive background in critical care and home health.',
  image: 'https://i.pravatar.cc/300?img=11'
}, {
  name: 'Elena Rodriguez',
  role: 'Head of Therapy Services',
  bio: 'Elena develops innovative rehabilitation programs that empower our patients to regain independence and improve their quality of life.',
  image: 'https://i.pravatar.cc/300?img=5'
}, {
  name: 'David Thompson',
  role: 'Patient Experience Coordinator',
  bio: 'David is dedicated to ensuring every family feels supported, heard, and guided throughout their entire journey with Crownehealth.',
  image: 'https://i.pravatar.cc/300?img=68'
}];
export function AboutPage() {
  return <div className="bg-nude-light min-h-screen" data-id="element-220">
      <PageHeader title="About Crownehealth" subtitle="Discover the heart behind our care and the people dedicated to your well-being." data-id="element-221" />

      {/* Mission & Story */}
      <section className="py-20" data-id="element-222">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-223">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-224">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative rounded-[2rem] overflow-hidden shadow-soft-lg aspect-[4/3]" data-id="element-225">
              <img src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&q=80&w=2000" alt="Healthcare professionals collaborating" className="w-full h-full object-cover" data-id="element-226" />
              <div className="absolute inset-0 bg-chocolate/10 mix-blend-overlay" data-id="element-227" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-228">
              <h2 className="text-3xl md:text-4xl font-serif text-chocolate mb-6" data-id="element-229">
                Our Story & Mission
              </h2>
              <div className="space-y-6 text-lg text-chocolate/80 leading-relaxed" data-id="element-230">
                <p data-id="element-231">
                  Crownehealth was founded on a simple yet profound belief: that
                  everyone deserves to receive care that honors their dignity,
                  respects their individuality, and nurtures their spirit.
                </p>
                <p data-id="element-232">
                  What began as a small team of dedicated nurses has grown into
                  a comprehensive healthcare provider, but our core philosophy
                  remains unchanged. We don't just treat conditions; we care for
                  people.
                </p>
                <p className="font-medium text-chocolate border-l-4 border-gold pl-4 italic" data-id="element-233">
                  "Our mission is to elevate the standard of care by combining
                  clinical excellence with unwavering compassion, ensuring every
                  individual we serve feels valued, safe, and truly seen."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16 bg-chocolate text-cream" data-id="element-234">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-235">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-236">
            {[{
            number: '25+',
            label: 'Years Experience'
          }, {
            number: '500+',
            label: 'Families Served'
          }, {
            number: '100+',
            label: 'Certified Staff'
          }, {
            number: '98%',
            label: 'Satisfaction Rate'
          }].map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-237">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gold mb-2" data-id="element-238">
                  {stat.number}
                </div>
                <div className="text-sm uppercase tracking-wider font-medium text-cream/80" data-id="element-239">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white" data-id="element-240">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-241">
          <div className="text-center mb-16" data-id="element-242">
            <h2 className="text-3xl md:text-4xl font-serif text-chocolate mb-4" data-id="element-243">
              Our Core Values
            </h2>
            <p className="text-lg text-chocolate/70 max-w-2xl mx-auto" data-id="element-244">
              These principles guide every decision we make and every
              interaction we have.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-245">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-nude-light rounded-3xl p-8 text-center shadow-soft" data-id="element-246">
                <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center text-gold mb-6 shadow-sm" data-id="element-247">
                  <value.icon size={28} data-id="element-248" />
                </div>
                <h3 className="text-xl font-serif font-bold text-chocolate mb-3" data-id="element-249">
                  {value.title}
                </h3>
                <p className="text-chocolate/70 leading-relaxed text-sm" data-id="element-250">
                  {value.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20" data-id="element-251">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-252">
          <div className="text-center mb-16" data-id="element-253">
            <h2 className="text-3xl md:text-4xl font-serif text-chocolate mb-4" data-id="element-254">
              Meet Our Leadership
            </h2>
            <p className="text-lg text-chocolate/70 max-w-2xl mx-auto" data-id="element-255">
              A team of dedicated professionals committed to delivering
              exceptional care.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-256">
            {team.map((member, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-3xl overflow-hidden shadow-soft group" data-id="element-257">
                <div className="aspect-square overflow-hidden" data-id="element-258">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-259" />
                </div>
                <div className="p-6" data-id="element-260">
                  <h3 className="text-xl font-serif font-bold text-chocolate mb-1" data-id="element-261">
                    {member.name}
                  </h3>
                  <div className="text-gold font-medium text-sm mb-4" data-id="element-262">
                    {member.role}
                  </div>
                  <p className="text-chocolate/70 text-sm leading-relaxed" data-id="element-263">
                    {member.bio}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <WhyChooseUs data-id="element-264" />
      <CTABanner data-id="element-265" />
    </div>;
}